import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography
} from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { AuditEvent, useAuditEvents } from "../api/hooks";
import { apiClient, extractApiError } from "../api/client";
import { useSettingsStore } from "../store/settingsStore";

function StatusChip({ event }: { event: AuditEvent }) {
  const ok = event.status >= 200 && event.status < 300;
  const warn = event.status >= 300 && event.status < 500;
  const color = ok ? "success" : warn ? "warning" : "error";
  const Icon = ok ? CheckCircleRoundedIcon : ErrorOutlineRoundedIcon;
  return (
    <Chip
      icon={<Icon fontSize="small" />}
      label={event.status}
      color={color}
      variant="outlined"
      size="medium"
      sx={{ minWidth: 72, fontWeight: 700 }}
    />
  );
}

export function ActivityStream() {
  const activityRefreshMs = useSettingsStore((state) => state.activityRefreshMs);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [methodFilter, setMethodFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [userFilter, setUserFilter] = useState<string>("");
  const [eventFilter, setEventFilter] = useState<string>("");
  const [exporting, setExporting] = useState<"" | "json" | "csv">("");

  const { data, isLoading, isError, error, isFetching } = useAuditEvents(
    rowsPerPage,
    page * rowsPerPage,
    activityRefreshMs,
    methodFilter || undefined,
    statusFilter ? Number(statusFilter) : undefined,
    userFilter || undefined,
    eventFilter || undefined,
    search || undefined
  );

  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  const handleExport = async (format: "json" | "csv") => {
    setExporting(format);
    try {
      const response = await apiClient.get(`/v1/system/audit/export`, {
        params: {
          format,
          limit: 5000,
          offset: 0,
          method: methodFilter || undefined,
          status: statusFilter ? Number(statusFilter) : undefined,
          user: userFilter || undefined,
          event: eventFilter || undefined,
          q: search || undefined
        },
        responseType: format === "csv" ? "blob" : "json"
      });

      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const blob =
        format === "csv"
          ? (response.data as Blob)
          : new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `audit-export-${ts}.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Audit export failed", err);
    } finally {
      setExporting("");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Registro Audit Sicurezza"
        subheader="Accessi, azioni e richieste UI/API (append-only)"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              disabled={exporting !== ""}
              onClick={() => void handleExport("json")}
            >
              {exporting === "json" ? "Export JSON..." : "Export JSON"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={exporting !== ""}
              onClick={() => void handleExport("csv")}
            >
              {exporting === "csv" ? "Export CSV..." : "Export CSV"}
            </Button>
            {isFetching ? <CircularProgress size={20} /> : null}
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="event-filter-label">Evento</InputLabel>
              <Select
                labelId="event-filter-label"
                value={eventFilter}
                label="Evento"
                onChange={(e: SelectChangeEvent) => {
                  setEventFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {["http.request", "auth.login_success", "auth.login_failed", "auth.alert.suspicious_failures"].map((eventName) => (
                  <MenuItem key={eventName} value={eventName}>
                    {eventName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              label="Cerca (path, UA, error)"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="method-filter-label">Metodo</InputLabel>
              <Select
                labelId="method-filter-label"
                value={methodFilter}
                label="Metodo"
                onChange={(e: SelectChangeEvent) => {
                  setMethodFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {["GET", "POST", "PUT", "DELETE"].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Stato</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Stato"
                onChange={(e: SelectChangeEvent) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {[200, 400, 401, 403, 404, 500].map((code) => (
                  <MenuItem key={code} value={String(code)}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              label="Utente"
              size="small"
              fullWidth
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
        </Grid>

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Caricamento log...
            </Typography>
          </Stack>
        ) : isError ? (
          <Alert severity="error">Impossibile recuperare il registro: {extractApiError(error)}</Alert>
        ) : events.length === 0 ? (
          <Alert severity="info">Nessuna attività registrata finora.</Alert>
        ) : (
          <>
            <TableContainer
              sx={{
                maxHeight: 560,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflowX: "auto"
              }}
            >
              <Table stickyHeader size="medium" aria-label="Registro attività">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 170 }}>Quando</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 90 }}>Metodo</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 100 }}>Stato</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 190 }}>Evento</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 190 }}>Utente</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 140 }}>IP</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 170 }}>Request ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 170 }}>Session ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 360 }}>Path</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 360 }}>User Agent</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", minWidth: 300 }}>Errore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((evt, idx) => (
                    <TableRow key={`${evt.timestamp}-${idx}`} hover>
                      <TableCell sx={{ fontSize: "0.95rem", verticalAlign: "top", whiteSpace: "nowrap" }}>
                        {evt.timestamp ? new Date(evt.timestamp).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.95rem", verticalAlign: "top", fontWeight: 700 }}>
                        {evt.method || "?"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.95rem", verticalAlign: "top" }}>
                        <StatusChip event={evt} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.95rem", verticalAlign: "top", whiteSpace: "nowrap" }}>
                        {evt.event || "http.request"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.95rem", verticalAlign: "top" }}>
                        {evt.user || "(utente sconosciuto)"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.95rem", verticalAlign: "top", whiteSpace: "nowrap" }}>
                        {evt.ip || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.95rem",
                          verticalAlign: "top",
                          fontFamily: "monospace",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {evt.request_id || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.95rem",
                          verticalAlign: "top",
                          fontFamily: "monospace",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {evt.session_id || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.95rem",
                          verticalAlign: "top",
                          fontFamily: "monospace",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word"
                        }}
                      >
                        {evt.path || "(path mancante)"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.95rem",
                          verticalAlign: "top",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word"
                        }}
                      >
                        {evt.user_agent || "sconosciuto"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.95rem",
                          verticalAlign: "top",
                          color: evt.error ? "error.main" : "text.secondary",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word"
                        }}
                      >
                        {evt.error || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                sx={{
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    fontSize: "1rem"
                  },
                  ".MuiTablePagination-select": {
                    fontSize: "1rem"
                  }
                }}
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
